import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { CartProvider } from "@/context/CartContext";
import { CostMarkupProvider } from "@/contexts/CostMarkupContext";
import { HomepageProductsProvider } from "@/contexts/HomepageProductsContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Header from "@/components/Header";

// Páginas públicas (homepage + catálogo carregam rápido — eagerly imported)
import Catalog from "@/pages/Catalog";
import CatalogPage from "@/pages/CatalogPage";

// Lazy-loaded (pesados ou raramente usados)
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Login = lazy(() => import("@/pages/Login"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Products = lazy(() => import("@/pages/admin/Products"));
const Pricing = lazy(() => import("@/pages/admin/Pricing"));
const MinQuantityRules = lazy(() => import("@/pages/admin/MinQuantityRules"));
const Categories = lazy(() => import("@/pages/admin/Categories"));
const Banners = lazy(() => import("@/pages/admin/Banners"));
const Homepage = lazy(() => import("@/pages/admin/Homepage"));
const Quotes = lazy(() => import("@/pages/admin/Quotes"));
const Sync = lazy(() => import("@/pages/admin/Sync"));

function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
    </div>
  );
}

function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen overflow-hidden bg-bg sm:overflow-visible">
      <Header />
      {children}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <CostMarkupProvider>
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Rotas públicas */}
              <Route
                path="/"
                element={
                  <PublicLayout>
                    <HomepageProductsProvider>
                      <Catalog />
                    </HomepageProductsProvider>
                  </PublicLayout>
                }
              />
              <Route
                path="/catalogo"
                element={
                  <PublicLayout>
                    <CatalogPage />
                  </PublicLayout>
                }
              />
              <Route
                path="/produto/:id"
                element={
                  <PublicLayout>
                    <ProductDetail />
                  </PublicLayout>
                }
              />
              <Route
                path="/carrinho"
                element={
                  <PublicLayout>
                    <Cart />
                  </PublicLayout>
                }
              />
              <Route path="/login" element={<Login />} />

              {/* Rotas admin (protegidas) */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="produtos" element={<Products />} />
                <Route path="precificacao" element={<Pricing />} />
                <Route path="regras-pedido" element={<MinQuantityRules />} />
                <Route path="categorias" element={<Categories />} />
                <Route path="banners" element={<Banners />} />
                <Route path="homepage" element={<Homepage />} />
                <Route path="orcamentos" element={<Quotes />} />
                <Route path="sincronizacao" element={<Sync />} />
              </Route>

              {/* 404 */}
              <Route
                path="*"
                element={
                  <PublicLayout>
                    <div className="mx-auto max-w-narrow px-6 py-20 text-center">
                      <h1 className="mb-2 text-4xl font-bold text-text-primary">
                        404
                      </h1>
                      <p className="text-text-secondary">
                        Página não encontrada.
                      </p>
                    </div>
                  </PublicLayout>
                }
              />
            </Routes>
          </Suspense>
          </ErrorBoundary>
          </CostMarkupProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
